import Highcharts from 'highcharts';
import { colors, fonts, fontSizes, fontWeights } from 'ui/theme';
import { AreaChartOptions, LineChartOptions } from './Chart.types';

const AREA_CHART_TYPE = 'area';
const LINE_CHART_TYPE = 'line';

const DEFAULT_LINEDASH_STYLE = 'ShortDash';

const baseChartStyle = {
  style: {
    fontFamily: fonts.sansSerif,
    fontSize: fontSizes.s14,
  },
};

const baseXAxis = {
  dateTimeLabelFormats: {
    minute: '%l:%M%P',
    hour: '%l:%M%P', // 1:05pm
    day: '%b %e', // June 6
  },
  labels: {
    style: {
      color: colors.textSecondary,
      fontSize: fontSizes.s14,
    },
  },
};

const baseYAxis = {
  min: 0,
  minRange: 1,
  title: {
    text: undefined,
  },
  labels: {
    format: '{value}',
    style: {
      color: colors.textSecondary,
      fontSize: fontSizes.s14,
    },
  },
};

const baseOptions = {
  credits: {
    enabled: false,
  },
  legend: {
    itemDistance: 60,
    margin: 20,
    itemStyle: {
      color: colors.textPrimary,
      fontWeight: `${fontWeights.regular}`,
      fontSize: fontSizes.s14,
    },
  },
  exporting: {
    enabled: false,
  },
  title: {
    text: undefined,
  },
};

/**
 * Generates base options for an area chart.
 * @param data
 * @param chartOptions
 * @param lineColor
 * @param name
 * @returns
 */
export const getAreaChartBaseOptions = ({
  lineColor = colors.cyan80,
  data,
  name,
  chartOptions = {},
}: AreaChartOptions): Highcharts.Options => {
  return {
    ...baseOptions,
    chart: {
      type: AREA_CHART_TYPE,
      ...baseChartStyle,
    },
    xAxis: {
      type: 'datetime',
      ...baseXAxis,
    },
    yAxis: {
      gridLineDashStyle: DEFAULT_LINEDASH_STYLE,
      ...baseYAxis,
    },
    tooltip: {
      xDateFormat: '%b %e',
      pointFormat: `<span>{series.name}:</span> <b>{point.y}</b><br/>`,
      backgroundColor: colors.grey80,
      borderRadius: 6,
      style: {
        color: colors.white,
      },
    },
    plotOptions: {
      area: {
        lineWidth: 1.5,
        lineColor,
        fillColor: {
          linearGradient: [0, 0, 0, 300] as unknown as Highcharts.LinearGradientColorObject,
          stops: [
            [0, lineColor],
            [1, Highcharts.color(lineColor).setOpacity(0).get('rgba') as string],
          ],
        },
        marker: {
          enabled: false,
          fillColor: lineColor,
        },
      },
    },
    series: [
      {
        type: AREA_CHART_TYPE,
        name,
        data,
      },
    ],
    ...chartOptions,
  };
};

/**
 * Generates base options for a line chart.
 * @param data
 * @param chartOptions
 * @returns
 */
export const getLineChartBaseOptions = ({
  data,
  chartOptions = {},
}: LineChartOptions): Highcharts.Options => {
  return {
    ...baseOptions,
    chart: {
      type: LINE_CHART_TYPE,
      ...baseChartStyle,
    },
    xAxis: {
      type: 'datetime',
      ...baseXAxis,
    },
    yAxis: {
      gridLineDashStyle: DEFAULT_LINEDASH_STYLE,
      ...baseYAxis,
    },
    series: data,
    ...chartOptions,
  };
};
